/**
 * The roles that only exist inside a school.
 *
 * These mirror api/application/config/constants.php -- ROLE_SCHOOL_ADMIN and
 * friends -- and the ids in master.role. They are numbered well clear of the
 * WiZi staff roles (1-4) and the B2C roles so the two sets cannot be confused.
 *
 * The value the panel reads is the EFFECTIVE role: jwt_admin_login_post returns
 * user.role_id already resolved from school_user, so a school user's stored
 * role_id is 20 or 21 and never the role sitting on their account row.
 */
export const ROLE_SUPER_ADMIN = 1;
export const ROLE_SCHOOL_ADMIN = 20;
export const ROLE_SCHOOL_TEACHER = 21;
export const ROLE_SCHOOL_STUDENT = 22;

export const getRoleId = (): number | null => {
    const raw = localStorage.getItem('role_id');
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
};

/** True for a user signed in against one school rather than against WiZi. */
export const isSchoolUser = (roleId: number | null = getRoleId()): boolean =>
    roleId === ROLE_SCHOOL_ADMIN || roleId === ROLE_SCHOOL_TEACHER;

export const isSchoolAdmin = (roleId: number | null = getRoleId()): boolean =>
    roleId === ROLE_SCHOOL_ADMIN;

export const isSchoolTeacher = (roleId: number | null = getRoleId()): boolean =>
    roleId === ROLE_SCHOOL_TEACHER;

export interface SchoolIdentity {
    id: number;
    code: string;
    name: string;
    status: string;
}

/**
 * The school this session belongs to, stored at login. NULL for WiZi staff.
 *
 * Display only. It is never sent to the API and must never be: the school a
 * request is routed to comes from the school_id claim inside the signed token,
 * so nothing a user can edit in localStorage changes which database they reach.
 */
export const getSchool = (): SchoolIdentity | null => {
    try {
        const raw = localStorage.getItem('school');
        return raw ? (JSON.parse(raw) as SchoolIdentity) : null;
    } catch {
        return null;
    }
};
